import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { useAuth } from '@/auth/AuthContext';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  disconnectConnection,
  issuePairing,
  listConnections,
  pluginDownloadHref,
  removeConnection,
  setConnectionDomain,
} from '@/data/connections';
import {
  buildPairingView,
  connectionDomainLabel,
  connectionRowModel,
  connectionVisibleUnder,
  publishModeModel,
  requiresPublishConfirm,
  PUBLISH_MODE_WARNING,
  UNASSIGNED_DOMAIN_VALUE,
  viewingFilterFor,
  type ConnectionsFilter,
  type ConnectionDto,
  type ConnectionRow,
  type DomainChoiceOption,
  type PairingView,
  type PublishMode,
} from '@/data/connections-model';
import { useActiveDomain } from '@/hooks/useActiveDomain';

/* ----------------------------------------------------------------------------
 * Settings › Connections — self-service "connect your WordPress site" surface.
 *
 * Flow (design §"Connect / pairing flow"):
 *   1. "Connect a WordPress site" -> POST /connector/pairings -> show the single-
 *      use pairing token (copy) + a 3-step checklist (download plugin, install,
 *      paste token). Token expires ~10 min; a Regenerate re-issues.
 *   2. Connected sites table (GET /connector/connections) with per-row Disconnect
 *      (DELETE /connector/connections/{id}, inline confirm).
 *   3. Per-site config (category / author / publish-mode) shown read-only with a
 *      Draft-by-default, double-gated publish control. No config-write endpoint
 *      is authorised yet, so a publish switch surfaces the warning locally and is
 *      not persisted — honest, never a fake save.
 *
 * SECURITY: the per-connection HMAC secret is never returned here; the row model
 * reads a safe whitelist so no secret can ever render (connections-model.ts).
 * -------------------------------------------------------------------------- */

export function ConnectionsPanel() {
  const { activeWorkspace } = useAuth();
  const qc = useQueryClient();
  const wsId = activeWorkspace?.id ?? 'none';

  // The same "Viewing: <domain>" projects that drive the shell selector. Each
  // connected site is explicitly assigned to one of these (or left Unassigned).
  const { domains, active, isAllDomains } = useActiveDomain();
  const domainChoices: DomainChoiceOption[] = useMemo(
    () => domains.map((d) => ({ projectId: d.projectId, hostname: d.hostname, displayName: d.displayName })),
    [domains],
  );

  // The per-domain list filter AUTO-FOLLOWS the current "Viewing" domain: a site
  // assigned to another domain is hidden, so the list never conflates domains.
  // "All domains" / "Unassigned" / another domain remain MANUAL overrides via the
  // dropdown; each shell "Viewing" change re-syncs the list back to that domain.
  const viewingFilter = viewingFilterFor({
    isAllDomains,
    activeProjectId: active?.projectId ?? null,
  });
  const [filter, setFilter] = useState<ConnectionsFilter>(viewingFilter);
  useEffect(() => {
    setFilter(viewingFilter);
  }, [viewingFilter]);

  const connections = useQuery({
    queryKey: ['connector', 'connections', wsId],
    queryFn: ({ signal }) => listConnections(signal),
  });

  return (
    <div className="space-y-5">
      <ConnectCard />
      <ConnectedSites
        state={connections}
        domains={domainChoices}
        filter={filter}
        onFilterChange={setFilter}
        onChanged={() => qc.invalidateQueries({ queryKey: ['connector', 'connections', wsId] })}
      />
    </div>
  );
}

// ---- Connect flow -----------------------------------------------------------

function ConnectCard() {
  const [view, setView] = useState<PairingView | null>(null);
  const [expired, setExpired] = useState(false);

  const mint = useMutation({
    mutationFn: issuePairing,
    onSuccess: (dto) => {
      setExpired(false);
      setView(
        buildPairingView({ dto, pluginDownloadHref: pluginDownloadHref(), issuedAtMs: Date.now() }),
      );
    },
  });

  // Flip to the "expired — regenerate" state when the token TTL elapses.
  useEffect(() => {
    if (!view) return;
    const ms = Math.max(0, view.expiresAtMs - Date.now());
    const t = setTimeout(() => setExpired(true), ms);
    return () => clearTimeout(t);
  }, [view]);

  return (
    <Card className="p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-text-primary">Connect a WordPress site</h3>
          <p className="mt-1 max-w-xl text-xs text-text-tertiary">
            Install the BookedCentral Connector plugin on your WordPress site and pair it with a
            single-use token — no SSH, no config edits, no secrets to copy by hand.
          </p>
        </div>
        {!view && (
          <Button
            variant="ai"
            onClick={() => mint.mutate()}
            disabled={mint.isPending}
            className="sm:shrink-0"
          >
            {mint.isPending ? 'Generating…' : 'Connect a WordPress site'}
          </Button>
        )}
      </div>

      {mint.isError && !view && (
        <p className="mt-3 text-sm text-danger">{issueErrorMessage(mint.error)}</p>
      )}

      {view && (
        <div className="mt-4 space-y-4">
          <TokenBlock token={view.token} expired={expired} expiresLabel={view.expiresLabel} />
          {expired ? (
            <div className="rounded-md border border-warning/30 bg-warning/5 px-4 py-3 text-sm text-text-secondary">
              This pairing token has expired. Generate a fresh one and paste it into the plugin.
            </div>
          ) : (
            <StepList steps={view.steps} />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => mint.mutate()} disabled={mint.isPending}>
              {mint.isPending ? 'Generating…' : expired ? 'Generate a new token' : 'Regenerate token'}
            </Button>
            <Button variant="ghost" onClick={() => { setView(null); setExpired(false); }}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function TokenBlock({ token, expired, expiresLabel }: { token: string; expired: boolean; expiresLabel: string }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [token]);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-text-secondary">Pairing token</span>
        <span className={cn('text-xs', expired ? 'text-danger' : 'text-text-tertiary')}>
          {expired ? 'Expired' : expiresLabel}
        </span>
      </div>
      <div className="flex items-stretch gap-2">
        <code
          data-testid="pairing-token"
          className={cn(
            'flex-1 truncate rounded-md border px-3 py-2 font-mono text-sm',
            expired
              ? 'border-border-subtle bg-inset text-text-tertiary line-through'
              : 'border-border-default bg-canvas text-text-primary',
          )}
        >
          {token}
        </code>
        <Button variant="secondary" onClick={copy} disabled={expired} aria-label="Copy pairing token">
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

function StepList({ steps }: { steps: PairingView['steps'] }) {
  return (
    <ol className="space-y-3">
      {steps.map((s) => (
        <li key={s.n} className="flex gap-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-inset text-[11px] font-semibold text-text-secondary">
            {s.n}
          </span>
          <div className="min-w-0">
            <div className="text-sm text-text-primary">
              {s.href ? (
                <a
                  href={s.href}
                  className="font-medium text-ai underline underline-offset-2 hover:text-ai-soft"
                  rel="noreferrer"
                >
                  {s.title}
                </a>
              ) : (
                <span className="font-medium">{s.title}</span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-text-tertiary">{s.detail}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---- Connected sites --------------------------------------------------------

function ConnectedSites({
  state,
  domains,
  filter,
  onFilterChange,
  onChanged,
}: {
  state: ReturnType<typeof useQuery<ConnectionDto[], unknown>>;
  domains: DomainChoiceOption[];
  filter: ConnectionsFilter;
  onFilterChange: (v: ConnectionsFilter) => void;
  onChanged: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = state;

  const rows = useMemo(() => (data ?? []).map((dto) => ({ dto, row: connectionRowModel(dto) })), [data]);
  const visible = useMemo(
    () => rows.filter(({ row }) => connectionVisibleUnder(row.projectId, filter)),
    [rows, filter],
  );

  return (
    <Card className="p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-text-primary">Connected sites</h3>
        {rows.length > 0 && domains.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-text-tertiary">
            Domain
            <select
              aria-label="Filter connections by domain"
              value={filter === 'all' ? 'all' : filter === 'unassigned' ? 'unassigned' : String(filter)}
              onChange={(e) => {
                const v = e.target.value;
                onFilterChange(v === 'all' ? 'all' : v === 'unassigned' ? 'unassigned' : Number(v));
              }}
              className="rounded-md border border-border-default bg-canvas px-2 py-1 text-xs text-text-primary"
            >
              <option value="all">All domains</option>
              <option value="unassigned">Unassigned</option>
              {domains.map((d) => (
                <option key={d.projectId} value={String(d.projectId)}>
                  {connectionDomainLabel(d.projectId, domains)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {isLoading && <LoadingState rows={2} />}

      {!isLoading && isError && <ErrorState error={error} onRetry={() => refetch()} />}

      {!isLoading && !isError && (!data || data.length === 0) && (
        <EmptyState
          title="No sites connected yet"
          teach="Connect a WordPress site above — once the plugin is paired, it appears here with its status and capabilities."
        />
      )}

      {!isLoading && !isError && data && data.length > 0 && visible.length === 0 && (
        <EmptyState
          title="No sites for this domain"
          teach="No connected site is assigned to this domain yet. Switch the filter to “All domains” to see every connection."
        />
      )}

      {!isLoading && !isError && visible.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-border-subtle text-left text-xs text-text-tertiary">
                <th className="py-2 pr-3 font-medium">Site</th>
                <th className="py-2 pr-3 font-medium">Domain</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Capabilities</th>
                <th className="py-2 pr-3 font-medium">Connected</th>
                <th className="py-2 pr-3 font-medium">Publishing</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {visible.map(({ dto, row }) => (
                <ConnectionRowView
                  key={dto.id}
                  dto={dto}
                  row={row}
                  domains={domains}
                  onChanged={onChanged}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const STATUS_TONE = { success: 'success', neutral: 'neutral', warning: 'warning' } as const;

function ConnectionRowView({
  dto,
  row,
  domains,
  onChanged,
}: {
  dto: ConnectionDto;
  row: ConnectionRow;
  domains: DomainChoiceOption[];
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false); // soft disconnect (disable)
  const [removing, setRemoving] = useState(false);     // hard remove (delete row)
  const [showConfig, setShowConfig] = useState(false);
  const isDisabled = row.statusTone === 'neutral';

  const disconnect = useMutation({
    mutationFn: () => disconnectConnection(row.id),
    onSuccess: () => {
      setConfirming(false);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: () => removeConnection(row.id),
    onSuccess: () => {
      setRemoving(false);
      onChanged();
    },
  });

  return (
    <>
      <tr className="border-b border-border-subtle/60 align-top">
        <td className="py-3 pr-3">
          <div className="font-medium text-text-primary">{row.label}</div>
          {row.url && <div className="truncate text-xs text-text-tertiary">{row.url}</div>}
        </td>
        <td className="py-3 pr-3">
          <DomainAssign connectionId={row.id} projectId={row.projectId} domains={domains} onChanged={onChanged} />
        </td>
        <td className="py-3 pr-3">
          <Badge tone={STATUS_TONE[row.statusTone]}>{row.statusLabel}</Badge>
        </td>
        <td className="py-3 pr-3">
          <div className="flex flex-wrap gap-1">
            {row.capabilityLabels.length === 0 ? (
              <span className="text-xs text-text-tertiary">—</span>
            ) : (
              row.capabilityLabels.map((c) => (
                <Badge key={c} tone="neutral">
                  {c}
                </Badge>
              ))
            )}
          </div>
        </td>
        <td className="py-3 pr-3 text-text-secondary">{row.connectedLabel}</td>
        <td className="py-3 pr-3">
          <button
            type="button"
            onClick={() => setShowConfig((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
          >
            <Badge tone={row.publishMode === 'draft' ? 'neutral' : 'warning'}>
              {row.publishMode === 'draft' ? 'Draft only' : 'Publish'}
            </Badge>
            <span className="underline underline-offset-2">{showConfig ? 'Hide' : 'Configure'}</span>
          </button>
        </td>
        <td className="py-3 text-right">
          {removing ? (
            <div className="inline-flex items-center gap-2">
              <span className="text-xs text-danger">Permanently remove this connection?</span>
              <Button
                variant="secondary"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
                className="text-danger"
              >
                {remove.isPending ? 'Removing…' : 'Remove permanently'}
              </Button>
              <Button variant="ghost" onClick={() => setRemoving(false)} disabled={remove.isPending}>
                Cancel
              </Button>
            </div>
          ) : confirming ? (
            <div className="inline-flex items-center gap-2">
              <span className="text-xs text-text-secondary">Disconnect this site?</span>
              <Button
                variant="secondary"
                onClick={() => disconnect.mutate()}
                disabled={disconnect.isPending}
                className="text-danger"
              >
                {disconnect.isPending ? 'Disconnecting…' : 'Confirm'}
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={disconnect.isPending}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2">
              {/* Active sites can be softly disconnected first; disabled sites only
                  need the hard Remove (surfaced prominently to clear lingering rows). */}
              {!isDisabled && (
                <Button variant="ghost" onClick={() => setConfirming(true)} className="text-danger">
                  Disconnect
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => setRemoving(true)}
                className="text-danger"
                aria-label={`Remove ${row.label}`}
              >
                Remove
              </Button>
            </div>
          )}
        </td>
      </tr>
      {(disconnect.isError || remove.isError) && (
        <tr>
          <td colSpan={7} className="pb-2 text-xs text-danger">
            {disconnect.isError
              ? disconnectErrorMessage(disconnect.error)
              : removeErrorMessage(remove.error)}
          </td>
        </tr>
      )}
      {showConfig && (
        <tr>
          <td colSpan={7} className="pb-4">
            <PerSiteConfig dto={dto} initialMode={row.publishMode} />
          </td>
        </tr>
      )}
    </>
  );
}

// ---- Per-site domain association (dropdown + badge) -------------------------

function DomainAssign({
  connectionId,
  projectId,
  domains,
  onChanged,
}: {
  connectionId: number;
  projectId: number | null;
  domains: DomainChoiceOption[];
  onChanged: () => void;
}) {
  const [value, setValue] = useState<number | null>(projectId);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setValue(projectId);
  }, [projectId]);

  const assign = useMutation({
    mutationFn: (next: number | null) => setConnectionDomain(connectionId, next),
    onSuccess: (res) => {
      if (res.persisted) {
        setValue(res.project_id);
        setEditing(false);
        onChanged();
      } else {
        // Honest: the gated migration has not landed — revert the optimistic pick.
        setValue(projectId);
      }
    },
    onError: () => setValue(projectId),
  });

  const notPersisted = assign.isSuccess && assign.data ? !assign.data.persisted : false;

  // Settled view: the assignment reads as a done fact (one clear badge), with
  // reassignment tucked behind a secondary action so the two never look ambiguous.
  if (!editing) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
          Assigned domain
        </span>
        <div className="flex items-center gap-2">
          <Badge tone={value == null ? 'warning' : 'success'}>{connectionDomainLabel(value, domains)}</Badge>
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={domains.length === 0}
            className="text-xs text-text-tertiary underline underline-offset-2 hover:text-text-secondary disabled:opacity-60"
          >
            Reassign
          </button>
        </div>
        {notPersisted && <span className="text-xs text-warning">Domain assignment isn’t available yet.</span>}
      </div>
    );
  }

  // Reassignment view: an explicit, clearly-secondary editor. Picking a domain
  // persists it (or reverts honestly pre-migration) and collapses back to settled.
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-text-tertiary">Reassign to domain</span>
      <div className="flex items-center gap-2">
        <select
          aria-label="Assign this site to a domain"
          value={value == null ? UNASSIGNED_DOMAIN_VALUE : String(value)}
          disabled={assign.isPending || domains.length === 0}
          onChange={(e) => {
            const raw = e.target.value;
            const next = raw === UNASSIGNED_DOMAIN_VALUE ? null : Number(raw);
            setValue(next);
            assign.mutate(next);
          }}
          className="rounded-md border border-border-default bg-canvas px-2 py-1 text-xs text-text-primary disabled:opacity-60"
        >
          <option value={UNASSIGNED_DOMAIN_VALUE}>Unassigned</option>
          {domains.map((d) => (
            <option key={d.projectId} value={String(d.projectId)}>
              {connectionDomainLabel(d.projectId, domains)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setValue(projectId);
          }}
          disabled={assign.isPending}
          className="text-xs text-text-tertiary underline underline-offset-2 hover:text-text-secondary disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
      {assign.isError && <span className="text-xs text-danger">Couldn’t save the domain. Try again.</span>}
    </div>
  );
}

// ---- Per-site config (read-only reflect + double-gated publish control) -----

function PerSiteConfig({ dto, initialMode }: { dto: ConnectionDto; initialMode: PublishMode }) {
  const [mode, setMode] = useState<PublishMode>(initialMode);
  const [pendingPublish, setPendingPublish] = useState(false);
  const model = publishModeModel(mode);

  function request(next: PublishMode) {
    if (requiresPublishConfirm(mode, next)) {
      setPendingPublish(true);
      return;
    }
    setPendingPublish(false);
    setMode(next);
  }

  return (
    <div className="rounded-md border border-border-subtle bg-inset/40 p-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Default category" value={dto.category ?? '—'} />
        <Field label="Default author" value={dto.author ?? '—'} />
      </div>

      <div className="mt-4">
        <span className="mb-1 block text-xs font-medium text-text-secondary">Publish mode</span>
        <div className="inline-flex rounded-md border border-border-default p-0.5">
          <ModeButton active={model.isDraft} onClick={() => request('draft')}>
            Draft
          </ModeButton>
          <ModeButton active={!model.isDraft} onClick={() => request('publish')}>
            Publish
          </ModeButton>
        </div>
        <p className="mt-2 text-xs text-text-tertiary">
          New content is created as a <strong className="text-text-secondary">Draft</strong> you review in
          WordPress. Draft-only is the default.
        </p>
      </div>

      {pendingPublish && (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning/5 p-3" role="alert">
          <p className="text-sm text-text-primary">Turn on live publishing?</p>
          <p className="mt-1 text-xs text-text-secondary">{PUBLISH_MODE_WARNING}</p>
          <div className="mt-3 flex items-center gap-2">
            <Button variant="secondary" onClick={() => { setMode('publish'); setPendingPublish(false); }}>
              I understand — switch to Publish
            </Button>
            <Button variant="ghost" onClick={() => setPendingPublish(false)}>
              Keep Draft only
            </Button>
          </div>
        </div>
      )}

      {model.mode === 'publish' && !pendingPublish && (
        <p className="mt-3 text-xs text-warning">{PUBLISH_MODE_WARNING}</p>
      )}

      <p className="mt-4 border-t border-border-subtle pt-3 text-xs text-text-tertiary">
        Per-site publishing configuration is managed by your workspace owner and syncs with the connector
        config service. Changes here preview the control; they are not saved from this screen yet.
      </p>
    </div>
  );
}

function ModeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded px-3 py-1 text-xs font-medium transition-colors',
        active ? 'bg-surface text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary',
      )}
    >
      {children}
    </button>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-text-secondary">{label}</span>
      <div className="rounded-md border border-border-default bg-canvas px-3 py-2 text-sm text-text-primary">
        {value}
      </div>
    </div>
  );
}

// ---- Error copy -------------------------------------------------------------

function issueErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'network_error') return 'Could not reach BookedCentral. Check your connection and try again.';
    if (err.status === 403) return 'Your role cannot connect new sites. Ask a workspace owner.';
    return err.message || 'Could not generate a pairing token. Please try again.';
  }
  return 'Could not generate a pairing token. Please try again.';
}

function disconnectErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'network_error') return 'Could not reach BookedCentral. The site was not disconnected — try again.';
    if (err.status === 404) return 'That site is already disconnected.';
    return err.message || 'Could not disconnect the site. Please try again.';
  }
  return 'Could not disconnect the site. Please try again.';
}

function removeErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'network_error') return 'Could not reach BookedCentral. The site was not removed — try again.';
    if (err.status === 404) return 'That site has already been removed.';
    return err.message || 'Could not remove the site. Please try again.';
  }
  return 'Could not remove the site. Please try again.';
}
